import { ImageName } from "@mcleod/images";
import {
    BlurEvent, Button, Checkbox, ClickEvent, Cursor, DataSourceAction, DataSourceExecutionEvent, DataSourceMode, Image,
    ImageType, KeyEvent, Label, Layout, LookupModelSearchEvent, LookupModelSelectionEvent, Panel, ScreenStack,
    SelectionType, Snackbar, TableRow, Textbox, Toast, ValidationResult
} from "@mcleod/components";
import { Alignment, Api, EmailUtil, Keys, LogManager, Model, ModelRow, StringUtil } from "@mcleod/core";
import { CarrierAssignment } from "./CarrierAssignment";
import { AutogenLayoutSendRateConfirmation } from "./autogen/AutogenLayoutSendRateConfirmation";
import { ContactVerificationUtil } from "./ContactVerificationUtil";
import { CommonTooltips } from "@mcleod/common";
import { ListenerListDef } from "@mcleod/components/src/base/ListenerListDef";

const log = LogManager.getLogger("lme.powerbroker.SendConfirmation");

export class SendRateConfirmation extends AutogenLayoutSendRateConfirmation {
    public doAfterRateConSent: () => void;
    private orderHistoryComment : string;

    private static readonly EMAIL_STATUS_TEMPLATE = `Email Status: {status}. {detail}.\n{emailAddress}`;
    private static readonly UNATTAINABLE_STATUS_TEMPLATE = `Email Status for {emailAddress} is currently unattainable due to no response from Highway.`;
    private static readonly EMAIL_VERIFICATION_FAILED = "The rate confirmation cannot be sent due to email status restrictions in the Highway control file.";
    private static SLIDE_OUT_PROPS = {
        width: 1200, height: window.innerHeight, backgroundColor: "white", top: 30, padding: 0,
        borderRadius: 4, borderWidth: 1, borderShadow: true, borderColor: "strokeSecondary"
    };

    override onLoad(): void {
        this.textboxCC.addlValidationCallback = (_value: string, mode: DataSourceMode): ValidationResult => {
            return this.emailDomainValidator(this.textboxCC, mode);
        };
    }

    static showSendRateConfirmationSlideout(buttonSendRateCon: Button, moveId: string, executeDoAfterAction?: () => void, slideoutProps = SendRateConfirmation.SLIDE_OUT_PROPS) {
        if (moveId != null) {
            Layout.getLayout("lme/powerbroker/SendRateConfirmation", { ...slideoutProps, width: 1000 }).addLayoutLoadListener(event => {
                const layout = event.target as SendRateConfirmation;
                layout.panelRateConData.paddingTop = 25;
                layout.buttonCancel.addClickListener(() => layout.slideOut());
                if (buttonSendRateCon) {
                    buttonSendRateCon.busy = true;
                }
                layout.search({ movement_id: moveId }).then(result => {
                    if (result?.modelRows?.length > 0) {
                        layout.doAfterRateConSent = (() => layout.slideOut().then(() => {
                            if (executeDoAfterAction != null) {
                                executeDoAfterAction();
                            }
                        }));
                        layout.setAllDataSourcesToMode(DataSourceMode.UPDATE);
                        layout.slideIn({ direction: Alignment.RIGHT }, true, { closeOnClickOff: false, greyedBackground: true });
                    }
                }).finally(() => {
                    if (buttonSendRateCon) {
                        buttonSendRateCon.busy = false;
                    }
                });
            });
        }
    }

    private emailDomainValidator(textbox: Textbox, mode: DataSourceMode) {
        if (mode === DataSourceMode.SEARCH) {
            return null;
        }
        if (StringUtil.isEmptyString(textbox.text)) {
            return { component: textbox, isValid: true };
        }
        const domains = textbox.text.split(",");
        for (const domain of domains) {
            if (!EmailUtil.isValidEmail(domain.trim())) {
                return { component: textbox, isValid: false, validationMessage: "Invalid domain format. Email domains must be in the following format [abc.com,xyz.com]" }
            }
        }
        return { component: textbox, isValid: true };
    }


    async sourceSendRateConfirmationAfterExecution(event: DataSourceExecutionEvent) {
        if (this.mainDataSource.data != null && this.mainDataSource.data.length > 0) {
            const manifestId = this.mainDataSource.data[0].get("manifest_id");
            const orderId = this.mainDataSource.data[0].get("order_id");
            if (manifestId != null)
                this.labelHeaderTitle.text = "Send Rate Confirmation - Manifest " + manifestId;
            else if (orderId != null)
                this.labelHeaderTitle.text = "Send Rate Confirmation - Order " + orderId;

            if (StringUtil.isEmptyString(this.textboxSubject.text))
                this.textboxSubject.text = this?.activeRow?.get("default_subject");
            if (StringUtil.isEmptyString(this.textboxCC.text))
                this.textboxCC.text = this?.activeRow?.get("email_cc");

            if (StringUtil.isEmptyString(this.textboxComment.text))
                this.textboxComment.text = this?.activeRow?.get("default_comment");
            ContactVerificationUtil.updateVerificationImage(this.activeRow.getFirstLookupModelData("default_contact"), this.imageCarrierVerification);
            this.reloadPreview(this.activeRow);
            const loadLockParams = {
                verification_category: this.activeRow.get("verification_category"),
                verification_status: this.activeRow.get("verification_status"),
                verification_detail: this.activeRow.get("verification_detail"),
                highway_access_url: this.activeRow.get("highway_access_url"),
                verification_action: this.activeRow.get("verification_action"),
                email_verified: this.activeRow.get("email_verified"),
                is_highway_active: this.activeRow.get("is_highway_active"),
                contact_verification_pref: this.activeRow.get("contact_verification_pref"),
                enable_order_hist_post: this.activeRow.get("enable_order_hist_post"),
            }
            this.fetchHighwayLoadLockDetails(loadLockParams);
            this.textboxCarrierId.quickInfoOverrideKeyValue = this.activeRow?.get("override_payee_id", null);
        }
    }

    reloadPreview(activeRow: ModelRow) {
        this.panelPreview.removeAll();

        /*this.displayData(activeRow, null, 0);*/
        if (this.activeRow.isNull("payee_id") && !this.activeRow.isNull("override_payee_id")) {
            this.activeRow.set("payee_id", this.activeRow.get("override_payee_id"));
        }

        if (activeRow.get("document_error") != null) {
            Snackbar.showWarningSnackbar(activeRow.get("document_error"));
        } else {
            const image = new Image({imageType: ImageType.PDF, fillHeight: true, fillRow: true, id: "rateConPreview"});
            image.imageBytes = activeRow.get("document");
            this.panelPreview.add(image);
        }
        const qualification_status = activeRow.get("qualification_status");
        if (qualification_status === "Disqualified") {
            this.imageError.visible = true;
            this.imageWarning.visible = false;
            CommonTooltips.setTooltipContentLayout(this.imageError, activeRow, "lme/dispatch/CarrierQualificationDetailsQuickInfo");
        }
        else if (qualification_status === "Warning") {
            this.imageError.visible = false;
            this.imageWarning.visible = true;
            CommonTooltips.setTooltipContentLayout(this.imageWarning, activeRow, "lme/dispatch/CarrierQualificationDetailsQuickInfo");
        }
        else {
            this.imageError.visible = false;
            this.imageWarning.visible = false;
        }
        const defaultEmail = this.activeRow.get("default_email_address");
        const defaultTemplate = this.activeRow.get("default_template");
        this.buttonSend.enabled = !StringUtil.isEmptyString(defaultEmail) && !StringUtil.isEmptyString(defaultTemplate) &&
            activeRow.get("qualification_status") != "Disqualified";
    }

    buttonSendLaterOnClick(event: ClickEvent) {
        this.mainDataSource.cancel();
    }

    textboxContactNameOnLookupModelSelection(event: LookupModelSelectionEvent) {
        if (event.hasSelection() === true) {
            const selectedRow = event.selection;
            ContactVerificationUtil.updateVerificationImage(selectedRow, this.imageCarrierVerification);
            this.mainDataSource.activeRow.set("default_email_address", event.selection.get("email"));
            this.searchPreview();
        }
    }

    searchPreview() {
        const template_number = this.mainDataSource.activeRow.getFirstLookupModelData("default_template")?.get("template_number", null);
        if (template_number != null) {
            Model.searchSingleRecord(this.mainDataSource.url, {
                movement_id: this.mainDataSource.activeRow.get("movement_id"),
                template_number: template_number,
                contact_name: this.mainDataSource.activeRow.get("default_contact"),
                email_address: this.mainDataSource.activeRow.get("default_email_address"),
                comment_text: this.textboxComment.text,
                email_cc: this.textboxCC.text
            }).then((record) => {
                this.mainDataSource.activeRow.set("warnings", record.get("warnings"));
                this.mainDataSource.activeRow.set("errors", record.get("errors"));
                this.mainDataSource.activeRow.set("qualification_status", record.get("qualification_status"));
                this.mainDataSource.activeRow.set("qualification_details", record.get("qualification_details"));
                this.reloadPreview(record);
                const _afterExecutionListenerDef: ListenerListDef = { listName: "_afterExecutionListeners" };
                const event = new DataSourceExecutionEvent(this.mainDataSource, DataSourceAction.UPDATE, true);
                this.mainDataSource.fireListeners(_afterExecutionListenerDef, event);
                if (this.validateSimple()) {
                    Api.post("lme/powerbroker/email-search",
                        {
                            movement_id: this.mainDataSource.activeRow.get("movement_id"),
                            order_id: this.mainDataSource.activeRow.get("order_id"),
                            email_address: this.textboxEmailAddress.text,
                            vendor_id: "H"
                        }
                    ).then((response) => {
                        const highwayLoadLockDetails = response?.data[0];
                        if (highwayLoadLockDetails) {
                            this.fetchHighwayLoadLockDetails(highwayLoadLockDetails);
                        }
                    });
                }
            });
        }
    }

    hasChangedData(layout: CarrierAssignment) {
        return this.activeRow != null && (this.activeRow.hasChanged() ||
            this?.activeRow?.get("default_subjlocect") != this.textboxSubject.text ||
            this?.activeRow?.get("default_comment") != this.textboxComment.text ||
            this?.activeRow?.get("default_contact") != layout.activeRow.get("carrier_contact") ||
            this?.activeRow?.get("default_email_address") != layout.activeRow.get("carrier_email"))
    }

    textboxContactNameBeforeLookupModelSearch(event: LookupModelSearchEvent) {
        if (this.activeRow.isNull("override_payee_id")) {
            event.preventDefault();
        } else {
            event.filter.parent_row_type = "P";
            event.filter.parent_row_id = this.activeRow.get("override_payee_id");
        }
    }

    /** This is an event handler for the onClick event of buttonSend.  */
    async buttonSendOnClick(event: ClickEvent) {
        if (this.validateSimple()) {
            this.buttonSend.busy = true;
            const body =
            {
                movement_id: this.mainDataSource.data[0].get("movement_id"),
                comment_text: this.textboxComment.text,
                template_number: this.textboxDefaultTemplate.getFirstLookupModelData().data["template_number"],
                contact_name: this.textboxContactName.text,
                subject: this.textboxSubject.text,
                email_cc: this.textboxCC.text,
                email_address: this.textboxEmailAddress.text
            }
            Api.post(this.mainDataSource.url, body, null, null).then(response => {
                log.info("posted");
                this.mainDataSource.cancel();
                if(!StringUtil.isEmptyString(this.orderHistoryComment)) {
                    this.addOrderHistComment();
                }
                Toast.showSuccessToast("Rate Confirmation Sent");
                setTimeout(() => {
                    if (this.doAfterRateConSent != null)
                        this.doAfterRateConSent();
                }, 2000);
            }).finally(() => this.buttonSend.busy = false);
        }
    }

    async textboxDefaultTemplateOnLookupModelSelection(event: LookupModelSelectionEvent) {
        if (event.hasSelection() === true) {
            let highwayLoadLockDetails;
            Model.searchSingleRecord(this.mainDataSource.url, {
                movement_id: this.mainDataSource.activeRow.get("movement_id"),
                template_number: event.selection.get("template_number"),
                contact_name: this.mainDataSource.activeRow.get("default_contact"),
                email_address: this.mainDataSource.activeRow.get("default_email_address"),
                comment_text: this.textboxComment.text,
                email_cc: this.textboxCC.text
            }).then((record) => {
                this.reloadPreview(record);
                if (this.validateSimple()) {
                    Api.post("lme/powerbroker/email-search",
                        {
                            movement_id: this.mainDataSource.activeRow.get("movement_id"),
                            order_id: this.mainDataSource.activeRow.get("order_id"),
                            email_address: this.textboxEmailAddress.text,
                            vendor_id: "H"
                        }
                    ).then((response) => {
                        highwayLoadLockDetails = response.data[0];
                        if (highwayLoadLockDetails) {
                            this.fetchHighwayLoadLockDetails(highwayLoadLockDetails);
                        }
                    });
                }
            })
        }
    }


    /** This is an event handler for the onBlur event of textboxComment. */
    async textboxRateConField(event: BlurEvent) {
        if (event.changedWhileFocused === true) {
            Model.searchSingleRecord(this.mainDataSource.url, {
                movement_id: this.mainDataSource.activeRow.get("movement_id"),
                template_number: this.mainDataSource.activeRow.getFirstLookupModelData("default_template")?.get("template_number", null),
                contact_name: this.mainDataSource.activeRow.get("default_contact"),
                email_address: this.mainDataSource.activeRow.get("default_email_address"),
                comment_text: this.textboxComment.text,
                email_cc: this.textboxCC.text
            }).then((record) => {
                this.reloadPreview(record);
            });
        }
    }

    async textboxEmailAddressOnBlur(event: BlurEvent) {
        if (event.changedWhileFocused) {
            this.searchPreview();
            const body = {
                movement_id: this.mainDataSource.data[0].get("movement_id"),
                order_id: this.mainDataSource.data[0].get("order_id"),
                payee_id: this.mainDataSource.data[0].get("override_payee_id"),
                move_status: this.mainDataSource.data[0].get("move_status"),
                email_address: this.textboxEmailAddress.text,
                contact_name: this.mainDataSource.data[0].get("default_contact"),
                vendor_id: "H"
            };
            const response = await Api.post("lme/powerbroker/email-search", body);
            if (response && response.data.length > 0) {
                await this.fetchHighwayLoadLockDetails(response?.data[0]);
            }
        }
        else if (this.validateSimple())
            this.buttonSend.enabled = true;
    }

    textboxEmailAddressOnKeyUp(event: KeyEvent) {
        // Don't disable if tabbing into email address textbox
        if (event.key === Keys.TAB && this.textboxEmailAddress.hasFocus)
            return;
        this.buttonSend.enabled = false;
    }

    private async verifiedEmail(response: any) {
        this.buttonSend.enabled = false;
        this.orderHistoryComment = "";
        let proceedVerificationAction = false ;
        try {
            if(response && !response.email_verified) {
                this.imageHighwayLogo.visible = false;
                this.labelImageVerification.visible = false;
                ContactVerificationUtil.updateVerificationImage(this.activeRow.getFirstLookupModelData("default_contact"), this.imageCarrierVerification);
                this.buttonSend.enabled = true;
                return;
            }

            if (response && !StringUtil.isEmptyString(response.verification_status)) {

                proceedVerificationAction = response.verification_action;

                this.labelImageVerification.visible = true;
                this.labelImageVerification.imageAlign = Alignment.LEFT;
                this.labelImageVerification.text = response.verification_status;
                if (StringUtil.equalsIgnoreCase(response.verification_status, "Verified")) {
                    this.labelImageVerification.imageName = ImageName.emailVerified;
                    this.labelImageVerification.color = "success";
                    this.createVerifiedHoverToDiscover(response, this.labelImageVerification);
                } else {

                    this.labelImageVerification.imageName = StringUtil.equalsIgnoreCase(response.verification_status, "Unverified") ? ImageName.emailUnverified : ImageName.emailStatusUnknown;
                    if(StringUtil.equalsIgnoreCase(response.verification_status, "Unverified")){
                        this.labelImageVerification.color = "error.dark";
                    }else{
                        this.labelImageVerification.color = "warning";
                    }
                    this.processResponseAction(response, this.textboxEmailAddress.text, response.enabledPostHist, proceedVerificationAction, false);
                }
            } else {
                this.labelImageVerification.visible = true;
                this.labelImageVerification.imageName = ImageName.triangleWarning;
                this.labelImageVerification.text = "No Response";
                this.labelImageVerification.color = "error.dark";
                this.processResponseAction(response, this.textboxEmailAddress.text, response.enabledPostHist, proceedVerificationAction, true);
            }

            this.imageHighwayLogo.visible = true;
            this.buttonSend.enabled = proceedVerificationAction;
            if(!this.buttonSend.enabled){
                this.buttonSend.disabledTooltip = SendRateConfirmation.EMAIL_VERIFICATION_FAILED;
            }

        } catch (error) {
            log.error("Error verifying email address for highway load lock");
        }
    }

    private processResponseAction(response: any, emailAddress: string, enabled_post_hist: any, proceedVerificationAction: any, unknownStatus: any) {
        if (proceedVerificationAction && enabled_post_hist && !StringUtil.isEmptyString(response.verification_status)) {
            if (!unknownStatus) {
                this.orderHistoryComment = SendRateConfirmation.EMAIL_STATUS_TEMPLATE
                    .replace('{status}', response.verification_status)
                    .replace('{detail}', response.verification_detail)
                    .replace('{emailAddress}', emailAddress);
            } else {
                this.orderHistoryComment = SendRateConfirmation.UNATTAINABLE_STATUS_TEMPLATE
                    .replace('{emailAddress}', emailAddress);
            }
        }
        this.createHoverToDiscover(response, this.labelImageVerification);
        this.labelImageVerification.allowTooltip = true;
        if(!proceedVerificationAction){
            const snackbarTarget = ScreenStack.getSnackbarTarget();
            snackbarTarget.dismissAllPopups();
            Snackbar.showWarningSnackbar(SendRateConfirmation.EMAIL_VERIFICATION_FAILED);
        }
    }

    private async addOrderHistComment() {
        const body = {
            order_history_comment: this.orderHistoryComment,
            movement_id: this.mainDataSource.data[0].get("movement_id")
        };
        try {
            await Api.post("lme/powerbroker/post-order-history-comment", body);
        } catch (error) {
            log.error("Error posting order history comment");
        }

    }


    createHoverToDiscover(response: any, imageEmailVerification: Label) {
        const verification_status = response.verification_status;
        const verification_detail = response.verification_detail;
        const highway_access_url = response.highway_access_url;
        imageEmailVerification.tooltipPosition = Alignment.LEFT;

        const panel = new Panel({ id: "panelHoverToDisplay", cursor: Cursor.DEFAULT }) as Panel;

        const labelOne = new Textbox({
            id: "hoverIdOne",
            caption: "Status",
            text: (StringUtil.isEmptyString(verification_status) ? "" : verification_status),
            printable: true,
            rowBreak: true
        });

        const labelTwo = new Textbox({
            id: "hoverIdTwo",
            caption: "Details",
            text: (StringUtil.isEmptyString(verification_detail) ? "" : verification_detail),
            printable: true,
            rowBreak: true
        });

        panel.add(labelOne);
        panel.add(labelTwo);

        if(!StringUtil.isEmptyString(highway_access_url)){
            const labelThree = new Label({
                id: "hoverIdThree",
                text: "View Highway contacts",
                link: highway_access_url,
                rowBreak: true,
                imageName: ImageName.highway_icon
            });
            panel.add(labelThree);
        }

        imageEmailVerification.tooltipDynamicContent = panel;
    }


    createVerifiedHoverToDiscover(response: any, imageEmailVerification: Label) {
        const verification_status = response.verification_status;

        const panel = new Panel({ id: "panelHoverToDisplay", cursor: Cursor.DEFAULT }) as Panel;

        const labelOne = new Textbox({
            id: "hoverIdOne",
            caption: "Status",
            text: (StringUtil.isEmptyString(verification_status) ? "" : verification_status),
            printable: true,
            rowBreak: true
        });

        panel.add(labelOne);
        imageEmailVerification.tooltipDynamicContent = panel;
    }

    public async fetchHighwayLoadLockDetails(response: any) {
        try {
            if (response && response.is_highway_active && !StringUtil.equalsIgnoreCase("N", response.contact_verification_pref)) {
                this.imageCarrierVerification.visible = false;
                await this.verifiedEmail(response);
            } else {
                this.buttonSend.enabled = this.buttonSend.enabled ? true : false;
                this.imageHighwayLogo.visible = false;
            }
            if (response && response.internal_verification != null) {
                if (!response.internal_verification) {
                    this.buttonSend.enabled = false;
                    this.buttonSend.disabledTooltip = "Contact does not pass the system's verification check.";
                }
            }
            const contactLookup = this.activeRow.getFirstLookupModelData("default_contact");
            if (contactLookup.get("email") == this.activeRow.get("default_email_address") && contactLookup.get("name") == this.activeRow.get("default_contact")) {
                ContactVerificationUtil.updateVerificationImage(this.activeRow.getFirstLookupModelData("default_contact"), this.imageCarrierVerification);
            } else {
                this.imageCarrierVerification.visible = false;
            }
        } catch (error) {
            return false;
        }
    }

    checkIfReadyToSend(tableRow: TableRow, sendRateCon: SendRateConfirmation, makeErrorImageTooltip: (modelRow: ModelRow) => Promise<Layout>) {
        const checkbox = tableRow.findComponentById("checkboxSelectRow") as Checkbox;
        const imageDisqualified = tableRow.findComponentById("imageDisqualified");
        if (tableRow.data.get("carrier_id") != null && tableRow.data.get("default_email_address") != null && tableRow.data.get("default_template") != null && sendRateCon.textboxEmailAddress.validateSimple() &&
            sendRateCon.textboxDefaultTemplate.validateSimple() && tableRow.data.get("qualification_status") != "Disqualified" && (!tableRow.data.get("email_verified") || tableRow.data.get("verification_action") == null || tableRow.data.get("verification_action"))) {
            const alreadyUnselectedEnabled = !tableRow.selected && tableRow.enabled;
            checkbox.enabled = true;
            imageDisqualified.visible = false;
            tableRow.enabled = true;
            if (!tableRow.selected && !alreadyUnselectedEnabled) {
                tableRow.table.selectRow(tableRow, SelectionType.DISTINCT, null);
            }
        }
        else {
            checkbox.enabled = false;
            imageDisqualified.visible = true;
            imageDisqualified.visible = true;
            imageDisqualified.tooltipDynamicContent = async () => {
                return await makeErrorImageTooltip(tableRow.data);
            }
            tableRow.enabled = false;
            if (tableRow.selected) {
                tableRow.table.selectRow(tableRow, SelectionType.DISTINCT, null);
            }
        }
    }
}
